import { useState } from 'react'

import { Button } from '@canyon/ui/Button'
import { RiCloseLine } from '@remixicon/react'
import { motion } from 'framer-motion'

import { type IInputProps, Input } from './Input'
import { SortableList } from './SortableList'
import { type ITagProps, Tag } from './Tag'

interface IInputChipsProps {
  canAlwaysRemove?: boolean
  chipVariant?: ITagProps['variant']
  chips: string[]
  disableDrag?: boolean
  disabled?: boolean
  onChange: (chips: string[]) => void
  placeholder?: string
  variant?: IInputProps['variant']
}

const Wrapper = ({ children }: { children: React.ReactNode }) => {
  return <div className="flex flex-col gap-4">{children}</div>
}

export const InputChips = ({
  canAlwaysRemove,
  chipVariant,
  chips,
  disableDrag,
  disabled,
  onChange,
  placeholder,
  variant,
}: IInputChipsProps) => {
  const [inputValue, setInputValue] = useState<string>('')

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()

      if (!inputValue.trim()) return

      if (!inputValue.includes('(') && inputValue.includes(',')) {
        onChange([...chips, ...inputValue.split(',').filter((input) => input)])
      } else {
        onChange([...chips, inputValue])
      }

      setInputValue('')
    }
  }

  const handleRemoveChip = (e: React.MouseEvent<HTMLButtonElement>, index: number) => {
    onChange(chips.filter((_, i) => i !== index))
  }

  const handleOnChange = (items: { id: string }[]) => {
    return onChange(
      items.map((item) => {
        const index = parseInt(item.id)
        return chips[index]
      })
    )
  }

  return (
    <Wrapper>
      <Input
        disabled={disabled}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        value={inputValue}
        variant={variant}
      />
      <div className="flex flex-wrap gap-x-1.5 gap-y-2.5">
        <SortableList
          items={chips.map((_, index) => ({ id: index.toString() }))}
          onChange={handleOnChange}
          renderItem={(item) => {
            const index = parseInt(item.id)
            const chip = chips[index]

            const ListItem = disableDrag ? 'div' : SortableList.Item

            return (
              <ListItem id={item.id}>
                <motion.div animate={{ opacity: 1 }} exit={{ opacity: 0 }} initial={{ opacity: 0 }}>
                  <motion.div className="relative" initial="normal" whileHover="hovered">
                    <Tag className="group relative" variant={chipVariant}>
                      <div className="flex items-center">
                        <span className="text-wrap">{chip}</span>
                        <motion.div
                          className="overflow-hidden flex-shrink-0"
                          transition={{
                            duration: 0.25,
                            ease: 'easeInOut',
                          }}
                          variants={{
                            hovered: { marginLeft: 4, opacity: 1, width: 'auto' },
                            normal: { marginLeft: 0, opacity: 0, width: 0 },
                          }}
                        >
                          {(!disabled || canAlwaysRemove) && (
                            <Button
                              onClick={(e) => handleRemoveChip(e, index)}
                              size="icon"
                              variant="ghost"
                            >
                              <RiCloseLine size={14} />
                            </Button>
                          )}
                        </motion.div>
                      </div>
                    </Tag>
                  </motion.div>
                </motion.div>
              </ListItem>
            )
          }}
        />
      </div>
    </Wrapper>
  )
}
